// workspace-repos-manifest-store.ts
//
// v3: workspace KHÔNG git → không có .gitmodules để biết "workspace có repo nào".
// Thay bằng .claude/repos.json do Avatar quản — nguồn sự thật DUY NHẤT về các repo
// code trong src/. Mỗi repo là git clone độc lập.
import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";

export const REPOS_MANIFEST_RELATIVE = ".claude/repos.json";

export interface RepoEntry {
  name: string; // tên thư mục trong src/ (vd "frontend")
  path: string; // relative từ workspace root (vd "src/frontend")
  url: string; // git remote URL
  addedAt: string; // ISO timestamp
}

interface ReposManifest {
  repos: RepoEntry[];
}

function manifestPath(workspaceRoot: string): string {
  return join(workspaceRoot, REPOS_MANIFEST_RELATIVE);
}

// Chuẩn hoá 1 entry thô (ticket #286). `repos.json` là file NGƯỜI DÙNG sửa được
// — bản viết tay, bản cũ, fixture test đều có thể thiếu field. MỌI nơi tiêu thụ
// manifest đều làm `join(workspaceRoot, r.path)`, nên `path` vắng làm node:path
// ném `The "path" argument must be of type string` và GIẾT CẢ LỆNH (đo 2026-08-19:
// `uninstall` lẫn `doctor` cùng chết, kể cả nhánh `--dry-run`).
//
// Chuẩn hoá Ở ĐÂY chứ không ở 8 call site: đây là nguồn sự thật DUY NHẤT về
// manifest — vá lẻ từng chỗ thì call site thứ 9 lại vỡ y hệt.
// `path` suy theo ĐÚNG quy ước nơi ghi (`clone-code-repo-into-src.ts`: `src/${name}`).
function normalizeRepoEntry(raw: unknown): RepoEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const name = typeof r.name === "string" ? r.name.trim() : "";
  // KHÔNG có `name` thì không suy ra được `path`, cũng không gọi tên được repo
  // trong bất kỳ log/summary nào ⇒ entry vô dụng, bỏ hẳn thay vì để nó nổ sau.
  if (!name) return null;
  const rawPath = typeof r.path === "string" ? r.path.trim() : "";
  // Chỉ chặn đúng nhánh SUY RA. `path` khai tường minh đi qua y như trước bản vá
  // — siết nó là đổi hành vi của manifest hợp lệ, ngoài phạm vi #286.
  // Nhánh suy ra thì `name` đi thẳng vào đường dẫn, nên `..` / dấu phân cách sẽ
  // trỏ RA NGOÀI `src/`: trước bản vá entry đó chết ở `join()`, giữ nguyên fallback
  // là biến cái chết đó thành đường dẫn traversal chạy được. Quy tắc giống
  // `validateRepoName` (clone-code-repo-into-src.ts) nhưng KHÔNG import từ đó —
  // file ấy đã import ngược module này, nối vào là vòng lặp import.
  if (!rawPath && (/[/\\]/.test(name) || name.includes(".."))) return null;
  return {
    name,
    path: rawPath || `src/${name}`,
    url: typeof r.url === "string" ? r.url : "",
    addedAt: typeof r.addedAt === "string" ? r.addedAt : "",
  };
}

export async function readReposManifest(workspaceRoot: string): Promise<RepoEntry[]> {
  const p = manifestPath(workspaceRoot);
  if (!(await pathExists(p))) return [];
  try {
    const data = await readJson<ReposManifest>(p);
    if (!Array.isArray(data.repos)) return [];
    return data.repos.map(normalizeRepoEntry).filter((r): r is RepoEntry => r !== null);
  } catch {
    return [];
  }
}

export async function addRepoToManifest(workspaceRoot: string, entry: RepoEntry): Promise<void> {
  const repos = await readReposManifest(workspaceRoot);
  // Replace nếu cùng name (idempotent), else append.
  const filtered = repos.filter((r) => r.name !== entry.name);
  filtered.push(entry);
  await writeJsonAtomic(manifestPath(workspaceRoot), { repos: filtered });
}

export async function removeRepoFromManifest(workspaceRoot: string, name: string): Promise<void> {
  const repos = await readReposManifest(workspaceRoot);
  await writeJsonAtomic(manifestPath(workspaceRoot), {
    repos: repos.filter((r) => r.name !== name),
  });
}

// Check tên repo đã tồn tại trong manifest chưa (tránh trùng).
export async function repoNameExists(workspaceRoot: string, name: string): Promise<boolean> {
  const repos = await readReposManifest(workspaceRoot);
  return repos.some((r) => r.name === name);
}
